import React, { useState, useEffect } from 'react'
import { Button, FormControl } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import styles from './CustomDomainSelectorModal.module.css'
import { publishSiteToCustomDomain } from '../../services/custom-domain'
import { fetchUserData, showMessageModal } from '../../Manage/actions'
import { REGEX_DOMAIN } from '../../constants'
import CustomModal from '../CustomModal/CustomModal'
import SuccessBox from '../CreateUpdateSiteModal/components/SuccessBox'

const CustomDomainSelectorModal = ({ show, onHide, validatedDomains = [], site, onAddNewDomain, onUpdate }) => {
  const [selectedDomain, setSelectedDomain] = useState(null)
  const [publishing, setPublishing] = useState(false)
  const [successData, setSuccessData] = useState(null)
  const [customSubdomain, setCustomSubdomain] = useState('')
  const [subdomainError, setSubdomainError] = useState(null)
  const [validatingSubdomain, setValidatingSubdomain] = useState(false)
  const dispatch = useDispatch()
  const { userProfile } = useSelector((state) => state.manage)

  // Initialize custom subdomain to 'www' by default
  useEffect(() => {
    if (site) {
      setCustomSubdomain('www')
    }
  }, [site])

  const handleDomainSelect = (domain) => {
    setSelectedDomain(domain)
    if (customSubdomain && domain) {
      validateSubdomain(customSubdomain, domain.domain)
    }
  }

  const validateSubdomain = async (subdomain, domainName) => {
    // Use 'www' as default if subdomain is empty
    const subdomainToValidate = subdomain?.trim() || 'www'

    if (!domainName) {
      setSubdomainError(null)
      return true
    }

    if (!REGEX_DOMAIN.test(subdomainToValidate.toLowerCase())) {
      setSubdomainError('Please enter a valid subdomain (letters, numbers, and hyphens only)')
      return false
    }

    if (subdomainToValidate.length > 63) {
      setSubdomainError('Subdomain must be 63 characters or less')
      return false
    }

    setSubdomainError(null)
    return true
  }

  const handleSubdomainChange = (value) => {
    setCustomSubdomain(value)
    setSubdomainError(null)

    if (selectedDomain) {
      const timeoutId = setTimeout(() => {
        validateSubdomain(value, selectedDomain.domain)
      }, 3000)

      if (window.subdomainValidationTimeout) {
        clearTimeout(window.subdomainValidationTimeout)
      }
      window.subdomainValidationTimeout = timeoutId
    }
  }

  const handlePublish = async () => {
    if (!selectedDomain || !site || !customSubdomain) return

    // Validate subdomain before publishing
    const isValid = await validateSubdomain(customSubdomain, selectedDomain.domain)
    if (!isValid) return

    setPublishing(true)
    try {
      const currentSiteDomain = site?.subdomain || site?.link
      // Use 'www' as default if customSubdomain is empty
      const subdomainToUse = customSubdomain?.trim() || 'www'
      const previewDomain = `${subdomainToUse}.${selectedDomain.domain}`

      const formData = new FormData()
      formData.append('newDomain', previewDomain)

      const response = await publishSiteToCustomDomain({
        currentSiteDomain: currentSiteDomain,
        formData
      })

      if (response.data) {
        dispatch(fetchUserData(false))

        setSuccessData({
          site: response.data?.link,
          files: response.data?.files,
          userProfile: userProfile
        })
      }
    } catch {
      dispatch(
        showMessageModal({
          title: 'Error',
          message: 'Failed to publish to custom domain. Please try again.',
          success: false
        })
      )
    } finally {
      setPublishing(false)
    }
  }

  const handleAddNew = () => {
    onHide()
    onAddNewDomain()
  }

  const handleClose = () => {
    if (publishing) return
    setSelectedDomain(null)
    setCustomSubdomain('')
    setSubdomainError(null)
    onHide()
  }

  const handleSuccessClose = () => {
    setSuccessData(null)
    onHide()
    dispatch(fetchUserData(false))
  }

  const handleSuccessReset = (site) => {
    if (onUpdate && site) {
      onUpdate(site)
    }
    setSuccessData(null)
    onHide()
  }

  if (successData) {
    return (
      <CustomModal
        show={true}
        onHide={handleSuccessClose}
        centered
        size="md"
        className="success_modal"
        enforceFocus={false}
        mobileCloseButton={false}
      >
        <SuccessBox
          site={successData.site}
          files={successData.files}
          userProfile={successData?.userProfile}
          onClose={handleSuccessClose}
          onReset={handleSuccessReset}
        />
      </CustomModal>
    )
  }

  return (
    <CustomModal show={show} onHide={handleClose} centered size="md" enforceFocus={false} backdrop="static">
      <div className={styles.modalContainer}>
        <button className={styles.closeButton} onClick={handleClose} disabled={publishing}>
          <img src="/delete.png" alt="Close" width="16" height="16" />
        </button>

        <div className={styles.headerContent}>
          <h2 className={styles.modalTitle}>Publish to Custom Domain</h2>
          <p className={styles.modalDescription}>Select an existing domain or add a new one</p>
        </div>

        <div className={styles.domainList}>
          {validatedDomains.map((domain) => (
            <div
              key={domain.domain}
              className={`${styles.domainItem} ${selectedDomain?.domain === domain.domain ? styles.selected : ''}`}
              onClick={() => handleDomainSelect(domain)}
            >
              <div className={styles.domainInfo}>
                <div className={styles.domainName}>
                  {selectedDomain?.domain === domain.domain ? (
                    <div className={styles.editableDomain}>
                      <FormControl
                        type="text"
                        value={customSubdomain}
                        onChange={(e) => handleSubdomainChange(e.target.value)}
                        placeholder="www"
                        className={`${styles.subdomainInput} ${subdomainError ? styles.error : ''}`}
                        disabled={publishing || validatingSubdomain}
                        onClick={(e) => e.stopPropagation()}
                        maxLength={63}
                      />
                      <span className={styles.domainSuffix}>.{domain.domain}</span>
                    </div>
                  ) : (
                    `www.${domain.domain}`
                  )}
                </div>
                <div className={styles.domainStatus}>
                  {validatingSubdomain && selectedDomain?.domain === domain.domain && (
                    <span className={styles.validatingText}>Checking availability...</span>
                  )}
                </div>
                {subdomainError && selectedDomain?.domain === domain.domain && (
                  <div className={styles.subdomainError}>{subdomainError}</div>
                )}
              </div>
              <div className={styles.radioButton}>
                <input
                  type="radio"
                  checked={selectedDomain?.domain === domain.domain}
                  onChange={() => handleDomainSelect(domain)}
                />
              </div>
            </div>
          ))}
        </div>

        <div className={styles.addNewSection}>
          <button className={styles.addNewButton} onClick={handleAddNew} disabled={publishing}>
            <img src="/assets/icons/plus-circle.svg" alt="Add" className={styles.addIcon} />
            Add new custom domain
          </button>
        </div>

        <div className={styles.footerButtons}>
          <Button variant="secondary" onClick={handleClose} disabled={publishing}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handlePublish}
            disabled={!selectedDomain || publishing || !!subdomainError || validatingSubdomain}
          >
            {publishing ? 'Publishing...' : validatingSubdomain ? 'Validating...' : 'Publish'}
          </Button>
        </div>
      </div>
    </CustomModal>
  )
}

export default CustomDomainSelectorModal
